<template>
  <div class="ac-card-content">
    <div class="flex-row just-between plr-20 pt-20">
      <div class="flex-row">
        <img
          src="@shared/assets/img/general/ServiceTitanBlackLogo.svg"
          class="st-logo right-10"
          alt="logo"
        />
        <div
          v-if="isPending()"
          class="i-connected"
        >
          <div class="bullet" />
          <div class="label-info">
            Pending
          </div>
        </div>
      </div>
      <button
        type="button"
        @click="toggle(false)"
      >
        <img
          src="@shared/assets/img/general/svg/x-black.svg"
          class="img-size-16"
          alt="close"
        />
      </button>
    </div>
    <div class="ac-body-container">
      <div
        v-if="isPending()"
        class="pending-info"
      >
        <span class="icon-luc-Info sac__font-24 f-primary" />
        <div class="sac__font-14 med margin-left-4">
          We've received your Tenant ID. We will notify you via email when the next steps are ready.
        </div>
      </div>
      <div class="sac__font-16 semi-bold">
        Step 1 of 3
      </div>
      <div class="grid-column ai-c jc-s margin-top-8">
        <div class="sac__font-16">
          To finalize the integration, please enter the
          <span class="sac__font-16 med">Client ID</span> and
          <span class="sac__font-16 med">Client Secret ID</span>
          below:
        </div>
        <SACTooltipItem
          class="margin-top-4"
          color="#727A8F"
          :icon-size="20"
        >
          <template #tooltip>
            <div class="sac__font-12">
              You can find these credentials in ServiceTitan under
              <div class="sac__font-12 med">
                Integrations > SmartAC.com > Application Details.
              </div>
            </div>
          </template>
        </SACTooltipItem>
      </div>
      <div class="grid-row gap-10 margin-top-16">
        <div class="sac__font-16 semi-bold">
          Client ID
        </div>
        <SACInput
          field="clientId"
          :value="clientId"
          :change="change"
        />
        <div class="sac__font-16 semi-bold ">
          Client Secret ID
        </div>
        <SACInput
          field="clientSecret"
          :value="clientSecret"
          :change="change"
        />
        <div class="sac__font-16 semi-bold">
          Need help?
        </div>
        <div class="sac__font-16 margin-bottom-4">
          If you have any questions or need assistance during the setup process,
          please feel free to contact your Partner Success Manager (PSM),
          who is ready to assist you.
        </div>
        <div v-if="error" class="error-container">
          <span class="icon-luc-Info sac__font-24 tomato" />
          <div>
            <div class="sac__font-14 med">
              Integration Error:
            </div>
            <div class="sac__font-14 margin-top-4">
              Please check ServiceTitan integration settings.
              Visit ServiceTitan to verify configuration. If the problem persists
              please contact your Partner Success Manager (PSM).
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="client-secret-footer-container">
      <button
        type="button"
        class="disconnect"
        @click="disconnect"
      >
        Disconnect
      </button>
      <div class="flex-row align-center">
        <button
          type="button"
          class="cancel"
          @click="toggle(false)"
        >
          Cancel
        </button>
        <button
          type="button"
          :disabled="!isActive"
          :class="['connect', isActive ? '' : 'disabled']"
          @click="saveClientAndSecret"
        >
          Connect
        </button>
      </div>
    </div>
    <ProcessOverlay
      :show="loading"
      text="Processing"
      overlayClass="ac-overlay"
    />
  </div>
</template>

<script>
import SACInput from '@shared/components/sac/SACInput.vue';
import ProcessOverlay from '@shared/components/ProcessOverlay.vue';
import SACTooltipItem from '@shared/components/sac/SACTooltip.vue';

export default {
  name: 'ClientSecretInput',
  components: {
    SACTooltipItem,
    SACInput,
    ProcessOverlay,
  },
  props: {
    toggle: Function,
    update: Function,
    partnerSettings: Object,
  },
  data() {
    return {
      loading: false,
      clientId: '',
      clientSecret: '',
      loadingText: '',
      error: null,
    };
  },
  computed: {
    isActive() {
      return this.clientId && this.clientSecret;
    },
  },
  methods: {
    isPending() {
      const {
        ST_TENANT_ID = '',
        ST_TENANT_ADDED = false,
      } = this.partnerSettings;
      if (ST_TENANT_ID !== '' && ST_TENANT_ADDED) {
        return false;
      }
      if (ST_TENANT_ID !== '') {
        return true;
      }
      return true;
    },
    change(f, v) {
      this[f] = v;
    },
    async disconnect() {
      try {
        this.loading = true;
        await this.$store.dispatch('disconnectStIntegration');
        this.loading = false;
        this.toggle(false);
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    async saveClientAndSecret() {
      try {
        this.loading = true;
        const corpId = this.$store.state.admin.current.corporationId;
        const config = {
          instanceCorpId: corpId,
          clientId: this.clientId,
          clientSecret: this.clientSecret,
        };
        await this.$store.dispatch('saveClientSecret', config);
        this.loading = false;
        this.toggle(true, 'titan-map-membership', {});
      } catch (err) {
        this.error = err;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.pending-info {
  width: 100%;
  background-color: #D9EBFC;
  border: 1px solid #2766E4;
  display: flex;
  flex-direction: row;
  padding: 10px;
  align-items: center;
  margin: 20px 0;
}
.st-logo{
  width: 193px;
  height: 33px;
  object-fit: cover;
  &.right-10 {
    margin-right: 10px;
  }
}
.client-secret-header {
  padding: 29px 24px 19px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid $NBLUE10;
  width: 100%;
}
.i-connected {
  align-items: center;
  background-color: #FFE4AF;
  border-radius: 4px;
  display: inline-flex;
  flex: 0 0 auto;
  gap: 8px;
  padding: 1px 8px;
  .bullet {
    background-color: #C48500;
    border-radius: 4px;
    height: 8px;
    width: 8px;
  }
  .label-info {
    color: #C48500;
    font-weight: 600;
    font-size: 0.875rem;
  }
}
.error-container {
  display: grid;
  grid-auto-flow: column;
  gap: 16px;
  padding: 8px;
  border: 2px solid $TOMATO100;
  border-radius: 4px;
  background-color: $TOMATO10;
  align-items: center;
}
.client-secret-footer-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-top: 1px solid $N10;
  .disconnect {
    border: 2px solid $TOMATO100;
    color: $TOMATO100;
    padding: 10px;
    border-radius: 4px;
  }
  .cancel {
    border: 2px solid $NBLUE110;
    color: $NBLUE110;
    padding: 10px;
    border-radius: 4px;
  }
  .connect {
    margin-left: 15px;
    border: 2px solid $NBLUE110;
    background-color: $NBLUE110;
    padding: 10px;
    color: $WHITE;
    border-radius: 4px;
    &.disabled {
      cursor: default;
      background-color: $N40;
      border: 2px solid $N40;
    }
  }
}
</style>
